import { PlayerAhoyEvent } from '../player_ahoy_event';
import { PlayerAhoyCommonEvent } from '../player_ahoy_common_event';
import PlaybackBufferEvent from './playback_buffer_event';
import { PlayerEnvironment } from '../player_environment'

export default class BaseEvent {
  constructor(options = {}){
    this._tech = options.tech;
    this._data = options.data;
    this._uuid = options.uuid;
    this._autoplay = options.autoplay;
    this._ads = options.ads;
    this._playbackBufferEvent = new PlaybackBufferEvent({ tech: this._tech, data: this._data });
    this.setAhoyEventGlobalProperties();
  }

  bindEvents(){
    this.bindPause();
    this.bindResume();
    this.bindMeta();
    this.bindBuffer();
    this.bindError();
    this.bindStart();
    this.bindEnded();

    this.bindAdsRequest();
    this.bindAdLoaded();
    this.bindAdBuffer();
    this.bindAdStart();
    this.bindAdError();
    this.bindAdClick();
    this.bindAdComplete();
    this.bindAdSkipped();
    this.bindAdWatch();
    this.bindAdFirstQuartile();
    this.bindAdMidpoint();
    this.bindAdThirdQuartile();

    this.bindFullscreenChange();
  }

  tech(){
    return this._tech;
  }

  data(){
    return this._data;
  }

  autoplay(){
    return this._autoplay;
  }

  ads(){
    return this._ads;
  }

  uuid(){
    return this._uuid;
  }

  playbackBufferEvent() {
    return this._playbackBufferEvent;
  }

  bindPause(){
    this.tech().on("pause", function(){
      this.tech().trigger({ type: "vp-pause", id: this.data().videoId() });
    }.bind(this));
  }

  bindResume(){
    var willResume;

    this.tech().on("pause", function(){
      willResume = true;
    });

    this.tech().on("playing", function(){
      if (willResume) {
        this.tech().trigger({ type: "vp-resume", id: this.data().videoId() });
        willResume = undefined;
      }
    }.bind(this));
  }

  bindMeta(){
    this.tech().on("durationchange", function(){
      var metadata = this.tech().metadata();
      this.tech().trigger({ type: "vp-meta", id: this.data().videoId(), level: metadata.level, levels: metadata.levels, targetBitrate: metadata.targetBitrate });
    }.bind(this));
  }

  bindBuffer() {
    let buffering;
    let startBufferTime;

    const sendBuffer = () => {
      const timer = (Date.now() - startBufferTime) / 1000;

      if (buffering) {
        this.tech().trigger({
          type: 'vp-buffer',
          id: this.data().videoId(),
          seconds: timer,
          position: this.tech().currentTime(),
          timer: timer,
          videoSourceBitrate: this.tech().getVideoSourceBitrate(),
          videoSourceHeight: this.tech().getVideoSourceHeight(),
          videoSourceWidth: this.tech().getVideoSourceWidth(),
          playerHeight: this.tech().getPlayerHeight(),
          playerWidth: this.tech().getPlayerWidth(),
        });
        this.playbackBufferEvent().end();
        buffering = false;
      }
    };

    this.tech().on('waiting', () => {
      startBufferTime = Date.now();
      buffering = true;
      this.playbackBufferEvent().setCodec(this.getCodec());
      this.playbackBufferEvent().start();
    });

    this.tech().on('playing', sendBuffer);
    this.tech().on('pause', sendBuffer);
  }

  bindError(){
    this.tech().on("error", function(){
      this.tech().trigger({
        type: "vp-error",
        id: this.data().videoId(),
        errorCode: this.tech().errorCode(),
        errorMessage: this.tech().errorMessage(),
        videoDuration: this.data().videoDuration(),
        currentTime: this.tech().currentTime(),
        embed: this.data().embed()
      });
    }.bind(this));
  }

  bindStart(){
    var started;
    var bindStartCallback = function(){
      if(!started){
        this.tech().trigger({
          type: "vp-start",
          id: this.data().videoId(),
          embed: this.data().embed(),
          videoDuration: this.data().videoDuration(),
          hasAd: this.ads().paramsPresent(),
          autoplay: this.autoplay(),
          codec: this.getCodec(),
          is_drm: this.data().drmEnabled(),
          videoSourceBitrate: this.tech().getVideoSourceBitrate(),
          videoSourceHeight: this.tech().getVideoSourceHeight(),
          videoSourceWidth: this.tech().getVideoSourceWidth(),
          playerHeight: this.tech().getPlayerHeight(),
          playerWidth: this.tech().getPlayerWidth(),
        });
        started = true;
      }
    };

    this.tech().one("playing", function(){
      bindStartCallback.apply(this);
    }.bind(this));

    this.tech().one("adstart", function(){
      bindStartCallback.apply(this);
    }.bind(this));

    this.tech().one("adserror", function(){
      bindStartCallback.apply(this);
    }.bind(this));
  }

  getCodec(){
    if(!this.data().drmEnabled() && this.tech().hasDashObject()){
      return 'vp9';
    }

    return 'h264';
  }

  bindEnded() {
    this.tech().on('ended', () => {
      this.tech().trigger('vp-ended');
    });
  }

  bindAdsRequest() {
    this.tech().on('ads-request-loaded', (event) => {
      const props = {
        content_id: event.videoId,
        ad_tag: event.currentAdTag,
        videopremier: event.premier,
        embed: event.embed,
        ad_source: 'dfp',
        play_uuid: event.playUUID,
        content_type: event.contentType
      };

      if (event.adTagIsVmap) {
        PlayerAhoyEvent.sendAdRuleRequest(
          Object.assign(props, {
            ad_playlist_id: event.adPlaylistId
          })
        );
      } else {
        PlayerAhoyEvent.sendAdsRequest(
          Object.assign(props, {
            ad_uuid: this.ads().uuid()
          })
        );
      }
    });
  }

  bindAdLoaded() {
    this.tech().on('adloaded', (e) => {
      this.ads().clearUUID();
      this.sendAdLoadedEvent(e.ad);
    });
  }

  sendAdLoadedEvent(ad) {
    PlayerAhoyEvent.sendAdLoaded({
      video_id: this.data().videoId(),
      ad_uuid: this.ads().uuid(),
      play_uuid: this.data().playUUID(),
      content_type: this.data().type(),
      ad_playlist_id: this.ads().adPlaylistId(),
      ad_tag: this.ads().currentAdTag(),
      is_linear: ad.isLinear(),
      duration: ad.getDuration(),
      is_skippable: ad.isSkippable(),
      ad_id: ad.getAdId(),
      ad_system: ad.getAdSystem(),
      advertiser_name: ad.getAdvertiserName(),
      api_framework: ad.getApiFramework(),
      ad_content_type: ad.getContentType(),
      creative_id: ad.getCreativeId(),
      title: ad.getTitle(),
      width: ad.getWidth(),
      height: ad.getHeight(),
      vast_media_width: ad.getVastMediaWidth(),
      vast_media_height: ad.getVastMediaHeight(),
      vast_media_bitrate: ad.getVastMediaBitrate(),
      wrapper_creative_ids: ad.getWrapperCreativeIds(),
      wrapper_ad_ids: ad.getWrapperAdIds(),
      wrapper_ad_systems: ad.getWrapperAdSystems(),
      trafficking_parameters_string: ad.getTraffickingParametersString(),
      pod_index: ad.getPodIndex(),
      pod_time_offset: ad.getPodTimeOffset(),
      pod_total_ads: ad.getPodTotalAds(),
      pod_ad_position: ad.getPodAdPosition(),
      min_suggested_duration: ad.getMinSuggestedDuration(),
      skip_time_offset: ad.getSkipTimeOffset(),
      deal_id: ad.getDealId()
    });
  }

  bindAdBuffer() {
    this.tech().on("adbuffer", (e) => {
      PlayerAhoyCommonEvent.sendAdBuffer({
        video_id: this.data().videoId(),
        ad_uuid: this.ads().uuid(),
        play_uuid: this.data().playUUID(),
        content_type: this.data().type(),
        ad_playlist_id: this.ads().adPlaylistId(),
        ad_tag: this.ads().currentAdTag(),
        current_time: e.currentTime
      });
    });
  }

  bindAdStart(){
    this.tech().on("adstart_linear", (e) => {
      this.ads().setCurrentAdType(e.adType);
      PlayerAhoyEvent.sendAdStart({
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        ad_uuid: this.ads().uuid(),
        ad_content_type: e.adEvent.getAd().getContentType(),
        ad_playlist_id: this.ads().adPlaylistId(),
        current_time: this.tech().currentTime(),
        embed: this.data().embed() === 'true',
        play_uuid: this.data().playUUID()
      });
    });
  }

  bindAdError(){
    this.tech().plugin().on('adserror', (e) => {
      const properties = {
        play_uuid: this.data().playUUID(),
        embed: this.data().embed() === 'true',
        ad_uuid: this.ads().uuid(),
        ad_tag: this.ads().currentAdTag(),
        video_duration: this.tech().duration(),
        current_time: this.tech().currentTime(),
        message: e.data.AdError.getMessage(),
      };

      PlayerAhoyEvent.sendAdError(Object.assign(properties, {
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        error_code: e.data.AdError.getErrorCode(),
        error_type: e.data.AdErrorEvent.getError().getType()
      }));
    });
  }

  bindAdClick(){
    this.tech().on("adclick", function(e){
      this.adClickEvent(this.ads().currentAdType(), e.currentTime);
    }.bind(this));
  }

  adClickEvent(adType, currentTime) {
    const properties = {
      content_type: this.data().type(),
      play_uuid: this.data().playUUID(),
      ad_uuid: this.ads().uuid()
    };

    PlayerAhoyEvent.sendAdClick(Object.assign(properties, {
      content_id: this.data().videoId(),
      position: currentTime
    }));
  }

  bindAdComplete() {
    this.tech().on('adcomplete', (e) => {
      PlayerAhoyEvent.sendAdComplete({
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        position: e.currentTime,
        play_uuid: this.data().playUUID(),
        ad_uuid: this.ads().uuid()
      });
    });
  }

  bindAdSkipped() {
    this.tech().on('adskipped', (e) => {
      PlayerAhoyEvent.sendAdSkipped({
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        position: e.currentTime,
        play_uuid: this.data().playUUID(),
        ad_uuid: this.ads().uuid()
      });
    });
  }

  bindAdWatch(){
    this.tech().on("adwatch", function(e){
      this.tech().trigger({ type: 'vp-adwatch', id: this.data().videoId(), duration: e.data.duration, currentTime: e.data.currentTime, adUUID: this.ads().uuid(), ad_type: this.ads().currentAdType() });
    }.bind(this));
  }

  bindAdFirstQuartile() {
    this.tech().on('adfirstquartile', () => {
      PlayerAhoyEvent.sendAdFirstQuartile({
        play_uuid: this.data().playUUID(),
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        ad_uuid: this.ads().uuid(),
        ad_tag: this.ads().currentAdTag()
      });
    });
  }

  bindAdMidpoint() {
    this.tech().on('admidpoint', () => {
      PlayerAhoyEvent.sendAdMidpoint({
        play_uuid: this.data().playUUID(),
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        ad_uuid: this.ads().uuid(),
        ad_tag: this.ads().currentAdTag()
      });
    });
  }

  bindAdThirdQuartile() {
    this.tech().on('adthirdquartile', () => {
      PlayerAhoyEvent.sendAdThirdQuartile({
        play_uuid: this.data().playUUID(),
        content_id: this.data().videoId(),
        content_type: this.data().type(),
        ad_uuid: this.ads().uuid(),
        ad_tag: this.ads().currentAdTag()
      });
    });
  }

  bindFullscreenChange() {
    this.tech().on('fullscreenchange', function(){
      if(this.tech().plugin().instance().isFullscreen()) {
        this.tech().trigger({
          type: 'vp-fullscreen-change',
          id: this.data().videoId(),
          embed: this.data().embed()
        });
      }
    }.bind(this));
  }

  setAhoyEventGlobalProperties() {
    let globalData = {
      page: PlayerEnvironment.locationHref(),
      videopremier: this.data().isPremier(),
      referrer: PlayerEnvironment.referrer(),
      is_preview: this.data().preview(),
      cdn: this.data().cdnName(),
      embed: this.data().embed() === 'true',
    };

    if (this.data().userIsPremier()) {
      globalData.user_type = 'premier';
    }

    if (this.data().isVideo()) {
      if (this.data().filmId() != 0) {
        globalData.film_id = this.data().filmId();
      }

      if (this.data().seasonId() != 0) {
        globalData.season_id = this.data().seasonId();
      }
    }
    PlayerAhoyEvent._globalAppendData = globalData;
    PlayerAhoyCommonEvent._globalAppendData = globalData;
  }
}
